import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Alert, Button, Divider, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import type { DataAccessLevel } from "@helpers/api";
import { NOTEBOOKS_EVENTS } from "@helpers/events";
import { assertIsError, normalizePathname } from "@helpers/utils";

import { generateFriendlyName } from "../../../utils/friendlyName";
import Wrapper from "../components/Wrapper";
import { isToolkitValidName } from "../helpers";
import type { StoreState } from "../state";
import { getMe, launchNewJob } from "../state/api/actionCreators";
import { getJupyterImages } from "../state/jupyterImages/actionCreators";
import type { NewJobParams } from "../types/api";
import NewNotebook from "./NewNotebook";

const HeaderDiv = styled("div")(() => ({
  display: "flex",
  // justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
}));

const BackButton = styled(IconButton)(() => ({
  position: "absolute",
  left: 0,
  top: "50%",
  transform: "translate(-100%, -50%)",
}));

const BelowHeaderDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const AboveSubmitDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(10),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const defaultJobConfig: NewJobParams = {
  accountId: "",
  sourceData: null,
  jobName: "",
  imageId: "jupyter/base-notebook",
  useLab: true,
  randomJobName: "",
  cpu: 1,
  mem: 1,
  gpu: 0,
  gpuMem: null,
};

const NewNotebookForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basename = normalizePathname(location.pathname)
    .split("/")
    .slice(0, -1)
    .join("/");
  const jupyterImages = useSelector((state: StoreState) => state.jupyterImages);

  const dispatch = useDispatch();

  const [newJobParams, setNewJobParams] = useState<NewJobParams>({
    ...defaultJobConfig,
    randomJobName: generateFriendlyName(),
  });

  const [dataAccessLevels] = useState<DataAccessLevel[]>([]);

  const [serverErrorMessage, setServerErrorMessage] = useState<string>();

  const updateRandomName = useCallback(() => {
    setNewJobParams({ ...newJobParams, randomJobName: generateFriendlyName() });
  }, [newJobParams]);

  useEffect(() => {
    (async () => {
      await dispatch(getMe() as any);
      await dispatch(getJupyterImages() as any);
    })();
  }, [dispatch]);

  const launchNotebookJob = async () => {
    tracker.event(NOTEBOOKS_EVENTS.CREATE_FORM_SUBMITTED);
    try {
      const jobParamsWithAccessLevels = { ...newJobParams, dataAccessLevels };
      await dispatch(launchNewJob(jobParamsWithAccessLevels) as any);
      tracker.event(NOTEBOOKS_EVENTS.CREATE_FORM_SUCCEEDED);
    } catch (err: unknown) {
      assertIsError(err);
      tracker.event(NOTEBOOKS_EVENTS.CREATE_FORM_FAILED);
      setServerErrorMessage(err.message);
      return;
    }
    navigate(basename);
  };

  const isJobNameInvalid = newJobParams.jobName
    ? isToolkitValidName(newJobParams.jobName)
    : false;

  const configValid =
    !isJobNameInvalid &&
    newJobParams.accountId &&
    newJobParams.imageId &&
    Boolean(newJobParams.sourceData?.length);

  return (
    <Wrapper>
      <form>
        <HeaderDiv>
          <Typography variant="h2">Creating a new notebook</Typography>
          {location.state && "canGoBack" in location.state && (
            <BackButton
              onClick={() => {
                tracker.event(NOTEBOOKS_EVENTS.CREATE_FORM_BACK_CLICKED);
                navigate(-1);
              }}
            >
              <ChevronLeftIcon />
            </BackButton>
          )}
        </HeaderDiv>
        <BelowHeaderDivider />
        <NewNotebook
          jupyterImages={jupyterImages}
          newJobParams={newJobParams}
          handleJobConfigChange={setNewJobParams}
          updateRandomName={updateRandomName}
        />
        {serverErrorMessage && (
          <Alert severity="error">{serverErrorMessage}</Alert>
        )}
        <AboveSubmitDivider />
        <SubmitButton
          onClick={launchNotebookJob}
          color="primary"
          variant="contained"
          disabled={!configValid}
        >
          Launch notebook
        </SubmitButton>
      </form>
    </Wrapper>
  );
};

export default NewNotebookForm;
